import { Dispatch, ReactElement, SetStateAction, useRef } from "react";
import { AlertsCounter } from "shared/components/dataDisplay/AlertsCounter";
import { Semantics } from "shared/components/drops/DropsBadge";
import { SemanticsIcon } from "shared/components/feedback/InfoMessage/SemanticsIcon";
import {
  BORDER_RADIUS,
  COLUMN_GAP,
} from "shared/components/feedback/InfoMessage/style";
import { Box, Flex, Skeleton, Text } from "shared/components/Spor";
import { ArrowIcon } from "shared/icons/ArrowIcon";
import { useThemeSemantic } from "shared/theme/themeSemantic";
import styled from "styled-components";

export const Title = styled.h3`
  padding: 0;
  margin: 0;
  font-family: "Vy Sans", sans-serif;
  font-weight: bold;
  font-size: 1rem;
  line-height: 1.33rem;
  text-align: start;
`;

const Subtitle = styled(Text)`
  padding: 0;
  margin: 0;
  font-family: "Vy Sans", sans-serif;
  font-weight: normal;
  font-size: 1rem;
  line-height: 1.33rem;
  text-align: start;

  /* Truncation: */
  display: block;
  text-overflow: ellipsis;
  width: 100%;
  white-space: nowrap;
  overflow: hidden;
`;

const ButtonWrapper = styled.button`
  border: none;
  padding: 0;
  margin: 0;
  background: transparent;
  outline: none;
  width: 100%;
  border-radius: ${BORDER_RADIUS}px;
  display: grid;
  grid-template-columns: auto 1fr auto;
  justify-items: start;
  align-items: center;
  column-gap: ${COLUMN_GAP}px;
  grid-column: 1 / span 2;
  grid-row: 1;

  &:focus {
    box-shadow: inset 0 0 0 2px accentcolor;
  }

  &:focus:not(:focus-visible) {
    box-shadow: none;
  }

  &:focus-visible {
    box-shadow: inset 0 0 0 2px accentcolor;
  }
`;

type ExpandableButtonProps = {
  open: boolean;
  setopen: Dispatch<SetStateAction<boolean>>;
  semantics: Semantics;
  title: string | ReactElement;
  subTitle?: string | ReactElement;
  hideIcon?: boolean;
  skeleton?: boolean;
  showExpand?: boolean;
  counter?: number;
};

export const ExpandableButton = ({
  open,
  setopen,
  semantics,
  title,
  subTitle,
  hideIcon,
  skeleton,
  showExpand,
  counter,
}: ExpandableButtonProps) => {
  const theme = useThemeSemantic(semantics);
  const textRef = useRef<HTMLParagraphElement>(null);
  const enabled = showExpand;
  return (
    <ButtonWrapper
      type="button"
      aria-expanded={open}
      aria-controls="collapsible_info_message_content"
      disabled={!enabled}
      onClick={() => setopen(!open)}
    >
      {!hideIcon && <SemanticsIcon semantics={semantics} />}
      <Flex
        gridColumn={2}
        gap="9px"
        alignItems="center"
        justifyContent="space-between"
      >
        {typeof title === "string" ? <Title>{title}</Title> : title}
        {Boolean(counter) && (
          <AlertsCounter alerts={counter!} semantics="transparent" />
        )}
      </Flex>
      <Box
        w="100%"
        overflow="hidden"
        gridColumn={2}
        gridRow={2}
        mt={hideIcon ? "6px" : "0px"}
      >
        {skeleton ? (
          <Skeleton height="18px" width="100%" />
        ) : (
          <Subtitle
            ref={textRef}
            variant="sm"
            color={theme.colorSemanticTextSecondary}
          >
            {subTitle}
          </Subtitle>
        )}
      </Box>
      <Flex alignItems="center" gap="6px">
        {enabled && (
          <ArrowIcon
            color={theme.colorSemanticTextSecondary}
            rotate={open ? -180 : 0}
          />
        )}
      </Flex>
    </ButtonWrapper>
  );
};
